import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Calculator, Loader2, Plus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, IDR, NUM, DATE, formatErr } from "@/lib/api";
import { canApprove, canEditProduction, useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function BatchEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id;

  const [batch, setBatch] = useState({ product_id: "", mix_design_id: "", target_qty: 100, good_qty: 0, reject_qty: 0, waste_qty: 0, operator: "", notes: "", status: "draft" });
  const [lines, setLines] = useState({ materials: [], labor: [], machines: [], overhead: [], environmental: [] });
  const [products, setProducts] = useState([]);
  const [mixDesigns, setMixDesigns] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [laborTypes, setLaborTypes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [ovhCats, setOvhCats] = useState([]);
  const [envCats, setEnvCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [prods, mds, mats, labs, mchs, ovh, env] = await Promise.all([
        api.get("/products"), api.get("/mix-designs"), api.get("/materials"),
        api.get("/labor-types"), api.get("/machines"),
        api.get("/overhead-categories"), api.get("/env-cost-categories"),
      ]);
      setProducts(prods.data); setMixDesigns(mds.data); setMaterials(mats.data);
      setLaborTypes(labs.data); setMachines(mchs.data);
      setOvhCats(ovh.data); setEnvCats(env.data);
      if (!isNew) {
        const { data } = await api.get(`/batches/${id}`);
        setBatch(data.batch);
        setLines({ materials: data.materials || [], labor: data.labor || [], machines: data.machines || [], overhead: data.overhead || [], environmental: data.environmental || [] });
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const readonly = !canEditProduction(user) || batch?.status === "posted" || batch?.status === "locked";

  const createBatch = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const { data } = await api.post("/batches", { ...batch, target_qty: Number(batch.target_qty), good_qty: Number(batch.good_qty), reject_qty: Number(batch.reject_qty), waste_qty: Number(batch.waste_qty) });
      toast.success("Batch dibuat");
      navigate(`/app/batches/${data.id}`);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const updateBatch = async (patch) => {
    try { const { data } = await api.patch(`/batches/${id}`, patch); setBatch(data); toast.success("Batch diperbarui"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const addLine = async (kind, payload) => {
    try {
      const { data } = await api.post(`/batches/${id}/lines/${kind}`, payload);
      setLines((l) => ({ ...l, [kind]: [...l[kind], data] }));
      toast.success("Baris ditambahkan");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const updateLine = async (kind, line_id, patch) => {
    try {
      const { data } = await api.patch(`/batches/${id}/lines/${kind}/${line_id}`, patch);
      setLines((l) => ({ ...l, [kind]: l[kind].map((x) => x.id === line_id ? data : x) }));
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteLine = async (kind, line_id) => {
    try {
      await api.delete(`/batches/${id}/lines/${kind}/${line_id}`);
      setLines((l) => ({ ...l, [kind]: l[kind].filter((x) => x.id !== line_id) }));
      toast.success("Baris dihapus");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totals = useMemo(() => {
    const materials = lines.materials.reduce((s, x) => s + Number(x.total_cost || 0), 0);
    const labor = lines.labor.reduce((s, x) => s + Number(x.total_cost || 0), 0);
    const mach = lines.machines.reduce((s, x) => s + Number(x.total_cost || 0), 0);
    const ovh_var = lines.overhead.filter((x) => x.is_variable).reduce((s, x) => s + Number(x.amount || 0), 0);
    const ovh_fix = lines.overhead.filter((x) => !x.is_variable).reduce((s, x) => s + Number(x.amount || 0), 0);
    const env = lines.environmental.filter((x) => x.billing_mode === "hpp").reduce((s, x) => s + Number(x.amount || 0), 0);
    const total = materials + labor + mach + ovh_var + ovh_fix + env;
    const denom = Number(batch.good_qty) > 0 ? Number(batch.good_qty) : Math.max(1, Number(batch.target_qty));
    return { materials, labor, mach, ovh_var, ovh_fix, env, total, per_unit: total / denom };
  }, [lines, batch]);

  const runHppActual = async () => {
    try {
      const { data } = await api.post("/hpp/calc-actual", { batch_id: id, save: true });
      toast.success("HPP Aktual dihitung");
      navigate(`/app/hpp/${data.id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const approveBatch = async () => {
    try { const { data } = await api.post(`/batches/${id}/approve`); setBatch(data); toast.success("Batch disetujui"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const postBatch = async () => {
    if (!window.confirm("Posting akan mengunci batch. Lanjutkan?")) return;
    try { const { data } = await api.post(`/batches/${id}/post`); setBatch(data); toast.success("Batch diposting"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (loading) return <div className="gc-card"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  if (isNew) {
    return (
      <form onSubmit={createBatch} className="space-y-5">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600"><ArrowLeft className="w-4 h-4" /> Kembali</button>
        <div><span className="gc-label">Produksi</span><h1 className="gc-h1 mt-1">Batch Baru</h1></div>
        <div className="gc-card grid md:grid-cols-2 gap-4">
          <div>
            <label className="gc-label block mb-1.5">Produk *</label>
            <select required className="gc-input" value={batch.product_id} onChange={(e) => setBatch({ ...batch, product_id: e.target.value })} data-testid="batch-product">
              <option value="">— Pilih —</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="gc-label block mb-1.5">Mix Design</label>
            <select className="gc-input" value={batch.mix_design_id || ""} onChange={(e) => setBatch({ ...batch, mix_design_id: e.target.value })} data-testid="batch-md">
              <option value="">— Tanpa Formula —</option>
              {mixDesigns.filter((m) => !batch.product_id || m.product_id === batch.product_id).map((m) => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
            </select>
          </div>
          <div>
            <label className="gc-label block mb-1.5">Target Unit *</label>
            <input type="number" min={1} required className="gc-input" value={batch.target_qty} onChange={(e) => setBatch({ ...batch, target_qty: e.target.value })} data-testid="batch-target" />
          </div>
          <div>
            <label className="gc-label block mb-1.5">Operator</label>
            <input className="gc-input" value={batch.operator || ""} onChange={(e) => setBatch({ ...batch, operator: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <label className="gc-label block mb-1.5">Catatan</label>
            <textarea className="gc-textarea" rows={2} value={batch.notes || ""} onChange={(e) => setBatch({ ...batch, notes: e.target.value })} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => navigate(-1)} className="gc-btn-ghost">Batal</button>
          <button type="submit" disabled={saving} className="gc-btn-primary" data-testid="btn-save-batch">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Buat Batch"}</button>
        </div>
      </form>
    );
  }

  return (
    <div className="space-y-5">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-600"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="gc-label">Batch #{batch.batch_no}</span>
          <h1 className="gc-h1 mt-1">{products.find((p) => p.id === batch.product_id)?.name}</h1>
          <p className="text-sm text-slate-500 mt-1">{DATE(batch.batch_date)} • Status: <span className="font-semibold text-emerald-700">{batch.status}</span></p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={runHppActual} className="gc-btn-ghost" data-testid="btn-calc-hpp"><Calculator className="w-4 h-4" /> Hitung HPP Aktual</button>
          {canApprove(user) && batch.status === "draft" && <button onClick={approveBatch} className="gc-btn-ghost" data-testid="btn-approve-batch"><CheckCircle2 className="w-4 h-4" /> Setujui</button>}
          {canApprove(user) && batch.status === "approved" && <button onClick={postBatch} className="gc-btn-primary" data-testid="btn-post-batch"><ShieldCheck className="w-4 h-4" /> Posting</button>}
        </div>
      </div>

      <div className="gc-card grid md:grid-cols-4 gap-4">
        <div>
          <label className="gc-label block mb-1.5">Target</label>
          <input type="number" disabled={readonly} className="gc-input" value={batch.target_qty} onChange={(e) => setBatch({ ...batch, target_qty: e.target.value })} onBlur={(e) => updateBatch({ target_qty: Number(e.target.value) })} />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Produk Baik</label>
          <input type="number" disabled={readonly} className="gc-input" value={batch.good_qty} onChange={(e) => setBatch({ ...batch, good_qty: e.target.value })} onBlur={(e) => updateBatch({ good_qty: Number(e.target.value) })} data-testid="batch-good" />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Produk Gagal</label>
          <input type="number" disabled={readonly} className="gc-input" value={batch.reject_qty} onChange={(e) => setBatch({ ...batch, reject_qty: e.target.value })} onBlur={(e) => updateBatch({ reject_qty: Number(e.target.value) })} data-testid="batch-reject" />
        </div>
        <div>
          <label className="gc-label block mb-1.5">Waste</label>
          <input type="number" disabled={readonly} className="gc-input" value={batch.waste_qty} onChange={(e) => setBatch({ ...batch, waste_qty: e.target.value })} onBlur={(e) => updateBatch({ waste_qty: Number(e.target.value) })} />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4"><div className="gc-label !text-emerald-700">Total Biaya</div><div className="font-cabinet text-2xl font-black text-emerald-700 mt-1">{IDR(totals.total)}</div></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4"><div className="gc-label">HPP/Unit (estimasi)</div><div className="font-cabinet text-2xl font-black text-slate-900 mt-1">{IDR(totals.per_unit)}</div><div className="text-[11px] text-slate-400 mt-1">Berdasarkan {NUM(batch.good_qty || batch.target_qty, 0)} unit baik</div></div>
        <div className="rounded-2xl bg-slate-900 text-white p-4"><div className="gc-label !text-emerald-300">Komposisi</div><div className="mt-2 text-xs space-y-1"><div className="flex justify-between"><span>Bahan</span><span>{IDR(totals.materials)}</span></div><div className="flex justify-between"><span>Tenaga Kerja</span><span>{IDR(totals.labor)}</span></div><div className="flex justify-between"><span>Mesin/OVH Var</span><span>{IDR(totals.mach + totals.ovh_var)}</span></div><div className="flex justify-between"><span>OVH Tetap</span><span>{IDR(totals.ovh_fix)}</span></div><div className="flex justify-between"><span>Lingkungan</span><span>{IDR(totals.env)}</span></div></div></div>
      </div>

      <div className="gc-card">
        <Tabs defaultValue="materials">
          <TabsList className="mb-4">
            <TabsTrigger value="materials" data-testid="tab-materials">Bahan</TabsTrigger>
            <TabsTrigger value="labor" data-testid="tab-labor">Tenaga Kerja</TabsTrigger>
            <TabsTrigger value="machines" data-testid="tab-machines">Mesin</TabsTrigger>
            <TabsTrigger value="overhead" data-testid="tab-overhead">Overhead</TabsTrigger>
            <TabsTrigger value="environmental" data-testid="tab-environmental">Lingkungan</TabsTrigger>
          </TabsList>

          <TabsContent value="materials">
            <LineEditor
              readonly={readonly}
              testIdBase="mat"
              lines={lines.materials}
              onAdd={() => addLine("materials", { material_id: materials[0]?.id, material_name: materials[0]?.name, std_qty: 0, actual_qty: 0, unit_price: materials[0]?.unit_price || 0 })}
              onDelete={(l) => deleteLine("materials", l.id)}
              onUpdate={(l, patch) => updateLine("materials", l.id, patch)}
              columns={[
                { key: "material_id", header: "Bahan", type: "select", options: materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}`, meta: m })), onChange: (val, opts) => ({ material_id: val, material_name: opts.meta?.name, unit_price: opts.meta?.unit_price || 0 }) },
                { key: "std_qty", header: "Std", type: "number", align: "right" },
                { key: "actual_qty", header: "Aktual", type: "number", align: "right" },
                { key: "unit_price", header: "Harga", type: "number", align: "right", format: IDR },
                { key: "total_cost", header: "Nilai", readonly: true, align: "right", format: IDR },
              ]}
            />
          </TabsContent>

          <TabsContent value="labor">
            <LineEditor
              readonly={readonly}
              testIdBase="lab"
              lines={lines.labor}
              onAdd={() => addLine("labor", { labor_type_id: laborTypes[0]?.id, labor_name: laborTypes[0]?.name, workers: 1, hours: 1, hourly_rate: laborTypes[0]?.hourly_rate || 0 })}
              onDelete={(l) => deleteLine("labor", l.id)}
              onUpdate={(l, patch) => updateLine("labor", l.id, patch)}
              columns={[
                { key: "labor_type_id", header: "Tenaga Kerja", type: "select", options: laborTypes.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}`, meta: m })), onChange: (val, opts) => ({ labor_type_id: val, labor_name: opts.meta?.name, hourly_rate: opts.meta?.hourly_rate || 0 }) },
                { key: "workers", header: "Orang", type: "number", align: "right" },
                { key: "hours", header: "Jam", type: "number", align: "right" },
                { key: "hourly_rate", header: "Tarif/Jam", type: "number", align: "right", format: IDR },
                { key: "total_cost", header: "Total", readonly: true, align: "right", format: IDR },
              ]}
            />
          </TabsContent>

          <TabsContent value="machines">
            <LineEditor
              readonly={readonly}
              testIdBase="mch"
              lines={lines.machines}
              onAdd={() => addLine("machines", { machine_id: machines[0]?.id, machine_name: machines[0]?.name, hours: 1, hourly_rate: machines[0]?.hourly_rate || 0, energy_cost: machines[0]?.energy_cost_per_hour || 0 })}
              onDelete={(l) => deleteLine("machines", l.id)}
              onUpdate={(l, patch) => updateLine("machines", l.id, patch)}
              columns={[
                { key: "machine_id", header: "Mesin", type: "select", options: machines.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}`, meta: m })), onChange: (val, opts) => ({ machine_id: val, machine_name: opts.meta?.name, hourly_rate: opts.meta?.hourly_rate, energy_cost: opts.meta?.energy_cost_per_hour }) },
                { key: "hours", header: "Jam", type: "number", align: "right" },
                { key: "hourly_rate", header: "Tarif", type: "number", align: "right", format: IDR },
                { key: "energy_cost", header: "Energi", type: "number", align: "right", format: IDR },
                { key: "total_cost", header: "Total", readonly: true, align: "right", format: IDR },
              ]}
            />
          </TabsContent>

          <TabsContent value="overhead">
            <LineEditor
              readonly={readonly}
              testIdBase="ovh"
              lines={lines.overhead}
              onAdd={() => addLine("overhead", { category_id: ovhCats[0]?.id, category_name: ovhCats[0]?.name, is_variable: ovhCats[0]?.is_variable ?? true, amount: 0 })}
              onDelete={(l) => deleteLine("overhead", l.id)}
              onUpdate={(l, patch) => updateLine("overhead", l.id, patch)}
              columns={[
                { key: "category_id", header: "Kategori", type: "select", options: ovhCats.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}`, meta: m })), onChange: (val, opts) => ({ category_id: val, category_name: opts.meta?.name, is_variable: opts.meta?.is_variable }) },
                { key: "is_variable", header: "Variabel?", type: "checkbox" },
                { key: "amount", header: "Nilai", type: "number", align: "right", format: IDR },
                { key: "note", header: "Catatan", type: "text" },
              ]}
            />
          </TabsContent>

          <TabsContent value="environmental">
            <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">Perhatikan mode pembebanan: <strong>HPP</strong> menambah biaya produksi; <strong>Report Only</strong> hanya untuk pelaporan (tidak masuk HPP) — mencegah <em>double counting</em>.</div>
            <LineEditor
              readonly={readonly}
              testIdBase="env"
              lines={lines.environmental}
              onAdd={() => addLine("environmental", { category_id: envCats[0]?.id, category_name: envCats[0]?.name, amount: 0, billing_mode: envCats[0]?.default_billing_mode || "hpp" })}
              onDelete={(l) => deleteLine("environmental", l.id)}
              onUpdate={(l, patch) => updateLine("environmental", l.id, patch)}
              columns={[
                { key: "category_id", header: "Kategori", type: "select", options: envCats.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}`, meta: m })), onChange: (val, opts) => ({ category_id: val, category_name: opts.meta?.name, billing_mode: opts.meta?.default_billing_mode }) },
                { key: "billing_mode", header: "Mode", type: "select", options: [{ value: "hpp", label: "HPP" }, { value: "report_only", label: "Report Only" }] },
                { key: "amount", header: "Nilai", type: "number", align: "right", format: IDR },
                { key: "description", header: "Deskripsi", type: "text" },
              ]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LineEditor({ lines, columns, onAdd, onUpdate, onDelete, readonly, testIdBase }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
          <tr>
            {columns.map((c) => <th key={c.key} className={`py-2 px-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.header}</th>)}
            {!readonly && <th></th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} className="border-t border-slate-100">
              {columns.map((c) => (
                <td key={c.key} className={`px-2 py-1.5 ${c.align === "right" ? "text-right" : ""}`}>
                  {c.readonly ? (c.format ? c.format(l[c.key]) : (l[c.key] ?? "-")) : (
                    c.type === "select" ? (
                      <select disabled={readonly} value={l[c.key] || ""} onChange={(e) => {
                        const opt = c.options.find((o) => o.value === e.target.value);
                        const patch = c.onChange ? c.onChange(e.target.value, opt || {}) : { [c.key]: e.target.value };
                        onUpdate(l, patch);
                      }} className="gc-input">
                        {c.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : c.type === "checkbox" ? (
                      <input type="checkbox" disabled={readonly} checked={!!l[c.key]} onChange={(e) => onUpdate(l, { [c.key]: e.target.checked })} className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    ) : (
                      <input disabled={readonly} type={c.type} step="any" defaultValue={l[c.key] ?? ""} onBlur={(e) => onUpdate(l, { [c.key]: c.type === "number" ? Number(e.target.value || 0) : e.target.value })}
                             className={`gc-input ${c.align === "right" ? "text-right" : ""}`} data-testid={`${testIdBase}-${c.key}-${l.id}`} />
                    )
                  )}
                </td>
              ))}
              {!readonly && <td className="px-2"><button onClick={() => onDelete(l)} className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-600"><Trash2 className="w-4 h-4" /></button></td>}
            </tr>
          ))}
          {!lines.length && (<tr><td colSpan={columns.length + 1} className="text-center py-6 text-slate-400 text-sm">Belum ada baris.</td></tr>)}
        </tbody>
      </table>
      {!readonly && <div className="mt-3"><button onClick={onAdd} className="gc-btn-ghost" data-testid={`btn-add-${testIdBase}`}><Plus className="w-4 h-4" /> Tambah Baris</button></div>}
    </div>
  );
}
